import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  CreateDoNotContactRequestMutation,
  CreateDoNotContactRequestMutationVariables,
  DoNotContactRequestAction,
  DoNotContactRequestSourceType,
} from "~frontend/__generated__/graphql";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  CREATE_DO_NOT_CONTACT_REQUEST,
  LIST_DO_NOT_CONTACT_REQUESTS,
} from "~frontend/graphql/doNotContactRequests";
import { phoneNumberIsValid } from "~frontend/util/phoneNumber";
import useMutation from "~frontend/util/useMutation";

export const AddContactFormSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, "Required")
    .refine(val => phoneNumberIsValid(val), "Must be valid phone number"),
  contactName: z.string().optional(),
  contactEmail: z.string().optional(),
  notes: z.string().optional(),
});

export type AddContactFormValues = z.infer<typeof AddContactFormSchema>;

export function AddContactModal({
  open,
  onClose,
  parentOnClose,
  phoneNumber,
  contactName,
  contactEmail,
}: {
  open: boolean;
  onClose: () => void;
  parentOnClose?: () => void;
  phoneNumber?: string;
  contactName?: string | null;
  contactEmail?: string | null;
}): ReactElement {
  const methods = useForm<AddContactFormValues>({
    resolver: zodResolver(AddContactFormSchema),
    defaultValues: {
      phoneNumber: phoneNumber ?? "",
      contactName: contactName ?? "",
      contactEmail: contactEmail ?? "",
      notes: "",
    },
  });

  const { control, handleSubmit, reset } = methods;

  const [createDoNotContactRequest] = useMutation<
    CreateDoNotContactRequestMutation,
    CreateDoNotContactRequestMutationVariables
  >(CREATE_DO_NOT_CONTACT_REQUEST, {
    onErrorSnackbarMessage: "Error adding contact to list",
    refetchQueries: [LIST_DO_NOT_CONTACT_REQUESTS],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (input: AddContactFormValues) => {
    await createDoNotContactRequest({
      variables: {
        input: {
          ...input,
          action: DoNotContactRequestAction.Add,
          sourceType: DoNotContactRequestSourceType.StaffManual,
        },
      },
    });
    resetAndClose();
    if (parentOnClose) {
      parentOnClose();
    }
  };

  const resetAndClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm">
      <DialogTitle>Add Contact to Do Not Contact List</DialogTitle>
      <DialogContent>
        <form>
          <RhfTextField
            control={control}
            name="phoneNumber"
            label="Phone number"
            bottomLabel=" "
            fullWidth
            inputRef={input => input && input.focus()} // a11y: sets focus here on modal open
          />
          <RhfTextField
            control={control}
            name="contactName"
            label="Contact name"
            optional={true}
            bottomLabel=" "
            fullWidth
          />
          <RhfTextField
            control={control}
            name="contactEmail"
            label="Contact email"
            optional={true}
            bottomLabel=" "
            fullWidth
          />
          <RhfTextField
            control={control}
            name="notes"
            label="Notes"
            optional={true}
            bottomLabel="Please include the reason for adding to the Do Not Contact List and links to any relevant tickets or slack threads."
            fullWidth
            multiline
            rows={4}
          />
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={resetAndClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          onClick={handleSubmit(onSubmit)}
        >
          Add contact to list
        </Button>
      </DialogActions>
    </Dialog>
  );
}
